import React, {useCallback, useEffect, useRef} from 'react';
import {useStyles} from "./Modal.style";
import ModalProperties from "./ModalProperties";
import CloseIcon from '@material-ui/icons/Close';
import {Dialog} from "@material-ui/core";
import PageLoading from "../PageLoading/PageLoading";
import {Observer} from "mobx-react-lite";
import clsx from "clsx";
import {useCommonContext} from "../../contexts/CommonContext";

const Modal: React.FC<ModalProperties> = ({
                                              className = '',
                                              onClose,
                                              onPressEnter = () => {},
                                              title,
                                              subtitle,
                                              open,
                                              loading = false,
                                              children,
                                              fullScreen = true,
                                              zIndex = 1300
                                          }) => {
    const classes = useStyles();
    const commonStore = useCommonContext();
    const paperRef = useRef<HTMLDivElement | null>(null);
    const dragStateRef = useRef<{startX: number, startY: number, baseX: number, baseY: number} | null>(null);

    const onModalClose = () => {
        commonStore.emitEvent('dialog_closed');
        onClose();
    }

    const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            if (onPressEnter) {
                onPressEnter();
            }
        }
    };

    useEffect(() => {
        if (open) {
            window.addEventListener('keydown', handleKeyPress);
        } else {
            window.removeEventListener('keydown', handleKeyPress);
        }
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    });

    useEffect(() => {
        if (!open) {
            commonStore.emitEvent('dialog_closed');
        }
    }, [open, commonStore])

    useEffect(() => {
        if (open && paperRef.current) {
            paperRef.current.style.transform = '';
        }
    }, [open]);

    const onDragMove = useCallback((event: MouseEvent) => {
        const dragState = dragStateRef.current;
        const paper = paperRef.current;
        if (!dragState || !paper) {
            return;
        }
        const x = dragState.baseX + (event.clientX - dragState.startX);
        const y = dragState.baseY + (event.clientY - dragState.startY);
        paper.style.transform = `translate(${x}px, ${y}px)`;
    }, []);

    const onDragEnd = useCallback(() => {
        dragStateRef.current = null;
        document.removeEventListener('mousemove', onDragMove);
        document.removeEventListener('mouseup', onDragEnd);
    }, [onDragMove]);

    const onHeaderMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest('[data-modal-no-drag]')) {
            return;
        }
        const paper = paperRef.current;
        if (!paper) {
            return;
        }
        const match = /translate\(([-\d.]+)px,\s*([-\d.]+)px\)/.exec(paper.style.transform);
        dragStateRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            baseX: match ? parseFloat(match[1]) : 0,
            baseY: match ? parseFloat(match[2]) : 0,
        };
        document.addEventListener('mousemove', onDragMove);
        document.addEventListener('mouseup', onDragEnd);
    }, [onDragMove, onDragEnd]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', onDragMove);
            document.removeEventListener('mouseup', onDragEnd);
        };
    }, [onDragMove, onDragEnd]);

    return (
        <Dialog
            style={{ zIndex: zIndex }}
            disableEnforceFocus
            PaperProps={!fullScreen ? {ref: paperRef} : undefined}
            maxWidth={false} fullScreen={fullScreen} onClose={onModalClose} open={open}>
            <div className={classes.header_container}
                 onMouseDown={!fullScreen ? onHeaderMouseDown : undefined}
                 style={!fullScreen ? {cursor: 'move'} : undefined}>
                <div className={classes.title}>
                    {title}
                    <span>{subtitle}</span>
                </div>
                <div className={classes.close_button} data-modal-no-drag onClick={onModalClose}>
                    <CloseIcon/>
                </div>
            </div>
            <div className={clsx(classes.content_container, className)}>
                {children}
                {loading && <Observer>{() => (<PageLoading isLoading={loading}/>)}</Observer>}
            </div>
        </Dialog>
    )
}
export default Modal;
